import { Component, input, forwardRef, signal } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { CommonModule } from "@angular/common";

export interface ButtonGroupOption<T = string> {
  label: string;
  value: T;
  disabled?: boolean;
}

/**
 * Button Group Component - Segmented control with single or multiple selection
 *
 * Features:
 * - Single or multiple selection mode
 * - Fully compatible with Angular Forms (ControlValueAccessor)
 * - Based on Spartan UI toggle-group
 * - Customizable options array
 * - Always requires at least one selection (cannot deselect if already selected)
 *
 * Usage:
 * ```html
 * <o2-button-group
 *   [options]="options"
 *   [multiple]="false"
 *   formControlName="selection"
 * />
 * ```
 *
 * With options:
 * ```typescript
 * options: ButtonGroupOption[] = [
 *   { label: 'Option 1', value: 'opt1' },
 *   { label: 'Option 2', value: 'opt2' },
 *   { label: 'Option 3', value: 'opt3' },
 * ];
 * ```
 */
@Component({
  selector: "o2-button-group",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex flex-wrap gap-2">
      @for (option of options(); track option.value) {
        <button
          type="button"
          [disabled]="option.disabled || disabled()"
          (click)="onButtonClick(option.value)"
          [class]="getButtonClass(option.value)"
          class="cursor-pointer px-2.5 rounded-sm text-sm text-[#585858] dark:text-gray-400 font-medium transition-all
                 bg-muted border border-transparent whitespace-nowrap
                 hover:bg-muted/80
                 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ option.label }}
        </button>
      }
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ButtonGroupComponent),
      multi: true,
    },
  ],
})
export class ButtonGroupComponent<T = string> implements ControlValueAccessor {
  // Inputs
  public readonly options = input.required<ButtonGroupOption<T>[]>();
  public readonly multiple = input<boolean>(false);
  public readonly disabled = input<boolean>(false);

  // Internal state
  protected readonly _value = signal<T | T[] | null>(null);

  // ControlValueAccessor callbacks
  private onChange: (value: T | T[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  /**
   * Handle button click
   * Always requires at least one selection - clicking a selected button does nothing
   */
  protected onButtonClick(value: T): void {
    if (this.disabled()) return;

    let newValue: T | T[] | null;

    if (this.multiple()) {
      // Multiple selection mode
      const currentArray = Array.isArray(this._value())
        ? [...(this._value() as T[])]
        : [];
      const index = currentArray.indexOf(value);

      if (index > -1) {
        // Cannot deselect if it's the last selected item
        if (currentArray.length > 1) {
          currentArray.splice(index, 1);
        } else {
          // Prevent deselection of the last item
          return;
        }
      } else {
        // Add to selection
        currentArray.push(value);
      }

      newValue = currentArray.length > 0 ? currentArray : null;
    } else {
      // Single selection mode - cannot deselect if already selected
      if (this._value() === value) {
        // Already selected, do nothing
        return;
      }
      newValue = value;
    }

    this._value.set(newValue);
    this.onChange(newValue);
    this.onTouched();
  }

  /**
   * Check if a value is selected
   */
  protected isSelected(value: T): boolean {
    const current = this._value();
    if (Array.isArray(current)) {
      return current.includes(value);
    }
    return current === value;
  }

  /**
   * Get button CSS classes based on selection state
   */
  protected getButtonClass(value: T): string {
    return this.isSelected(value)
      ? "!border !border-[#737373] dark:!border-gray-300 !text-black dark:!text-white"
      : "";
  }

  // ControlValueAccessor implementation
  writeValue(value: T | T[] | null): void {
    this._value.set(value);
  }

  registerOnChange(fn: (value: T | T[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // The disabled state is managed by the input signal
    // Angular Forms will update the input
  }
}
